import React, { useCallback, useState, useEffect } from 'react';
import { UseFormReturn, FieldValues } from 'react-hook-form';
import { isBefore, startOfDay, endOfDay } from 'date-fns';
import { ArrowUpIcon, PlusCircleIcon } from 'lucide-react';

import { CapacityType, EditableSession } from 'types/event.types';
import { getStartOfDayInTimezone, interpretDateInTimezone } from 'utils/dateTimeUtils';
import { formatTimezoneDisplay } from 'utils/format';
import { Form, InputField, DateTimePickerField, CheckboxField } from '@epoch-ui/components/Form';
import { ErrorMessage } from '@epoch-ui/components/ErrorMessage';
import { ButtonLink } from '@epoch-ui/components/ButtonLink';
import { Button } from '@epoch-ui/components/Button/button';
import {
  ActionButton,
  ActionButtonIcon,
  ActionButtonLabel,
  ActionButtonCaption
} from '@epoch-ui/components/ActionButton';
import {
  EventDescriptionField,
  EventFormatFieldsSection,
  CapacityModal,
  TargetAttendeesModal
} from 'pages/dashboard/overview/events/components';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { useEditEventDetailsSheet } from '@event-editor/hooks/eventEditorState';
import { useSessionTimeChangeValidations } from '@event-editor/components/EditEventDetailsSheet/validateEventTimeChanges';
import { TimeChangeWarningBanner } from '@event-editor/components/EditEventDetailsSheet/TimeChangeWarningBanner';
import {
  TimeChangeConfirmModal,
  TimeChangeConfirmModalType
} from '@event-editor/components/EditEventDetailsSheet/TimeChangeConfirmModal';

import { resetSessionTimeFields } from './sessionTimeUtils';

interface EventSessionFormProps extends React.HTMLAttributes<HTMLFormElement> {
  id: string;
  form: UseFormReturn<FieldValues>;
  disabled?: boolean;
  onSubmit?: (data: FieldValues) => void | Promise<void>;
  session?: EditableSession;
  /** Whether start time is before scheduled publish time */
  hasStartTimeBeforeScheduledPublishError?: boolean;
  /** Callback when user clicks "Update schedule" link */
  onUpdateScheduleClick?: () => void;
}

const EventSessionForm: React.FC<EventSessionFormProps> = ({
  id,
  form,
  onSubmit = (): void => undefined,
  disabled = false,
  session,
  hasStartTimeBeforeScheduledPublishError = false,
  onUpdateScheduleClick,
  ...props
}) => {
  const event = useEditableEvent();
  const timezoneCode = event.timezoneCode!;
  const editEventDetailsSheet = useEditEventDetailsSheet();
  // Disable start datetime fields if session is published and has already started
  const disableStartDateTime = session?.isPublished && session?.hasStarted;
  const [capacityModalOpen, setCapacityModalOpen] = useState(false);
  const [targetAttendeesModalOpen, setTargetAttendeesModalOpen] = useState(false);
  const [isTimeChangeModalOpen, setIsTimeChangeModalOpen] = useState(false);

  const handleTimeChangeReset = useCallback((): void => {
    resetSessionTimeFields(form, session, timezoneCode);
    setIsTimeChangeModalOpen(false);
  }, [form, session, timezoneCode]);

  // Effect to handle cross-field validations for startDateTime and endDateTime
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'startDateTime' || name === 'endDateTime') {
        form.trigger(['startDateTime', 'endDateTime']);
      }
    });

    return (): void => subscription.unsubscribe();
  }, [form]);

  const [allDay, capacity, targetAttendance, updatedStartDateTime, updatedEndDateTime] = form.watch(
    ['allDay', 'capacity', 'targetAttendance', 'startDateTime', 'endDateTime']
  );

  const timeChangeValidations = useSessionTimeChangeValidations(
    session?.id,
    updatedStartDateTime,
    updatedEndDateTime
  );
  const hasTimeChangeValidations = timeChangeValidations.length > 0;

  const disablePastDates = useCallback(
    (date: Date): boolean => {
      const today = getStartOfDayInTimezone(timezoneCode);
      const dateInTargetTz = interpretDateInTimezone(date, timezoneCode);
      return isBefore(dateInTargetTz, today);
    },
    [timezoneCode]
  );

  return (
    <React.Fragment>
      <Form {...form}>
        <form
          id={id}
          className="tw-flex tw-flex-col tw-gap-6 tw-px-6"
          onSubmit={
            onSubmit
              ? form.handleSubmit(onSubmit, (errors) => {
                  console.error(errors);
                })
              : undefined
          }
          {...props}
        >
          <InputField name="title" label="Session title" disabled={disabled} />

          <EventDescriptionField
            name="description"
            label="Session description"
            disabled={disabled}
            placeholder=" "
            compactToolbar
          />

          <div className="tw-flex tw-flex-col tw-gap-4">
            <div>
              <DateTimePickerField
                name="startDateTime"
                label="Start time"
                description={
                  <span>
                    {`You're scheduling the event in (${formatTimezoneDisplay(
                      timezoneCode,
                      'shortOffset'
                    )}) - ${formatTimezoneDisplay(timezoneCode, 'long')}. `}
                    <Button
                      variant="link"
                      size="none"
                      className="tw-p-0 tw-text-xs tw-no-underline hover:tw-underline"
                      onClick={editEventDetailsSheet.open}
                    >
                      Change timezone
                    </Button>
                  </span>
                }
                allDay={allDay}
                disabled={disabled || disableStartDateTime}
              >
                <DateTimePickerField.DateInput disabled={disablePastDates} />
                <DateTimePickerField.Time interval={5} />
              </DateTimePickerField>
              {hasStartTimeBeforeScheduledPublishError && (
                <ErrorMessage>
                  Start time cannot be before the event is scheduled to be published.{' '}
                  <ButtonLink
                    onClick={onUpdateScheduleClick}
                    className="tw-text-xs tw-font-normal tw-underline"
                  >
                    Update schedule
                  </ButtonLink>
                </ErrorMessage>
              )}
            </div>

            <DateTimePickerField
              name="endDateTime"
              label="End time"
              allDay={allDay}
              disabled={disabled}
            >
              <DateTimePickerField.DateInput disabled={disablePastDates} />
              <DateTimePickerField.Time interval={5} />
            </DateTimePickerField>
            <div>
              <CheckboxField
                name="allDay"
                label="All day"
                className="tw-mt-0"
                disabled={disabled}
                onCheckedChange={(checked): void => {
                  if (checked) {
                    const startDateTime = form.getValues('startDateTime');
                    const endDateTime = form.getValues('endDateTime');

                    if (startDateTime) {
                      form.setValue('startDateTime', startOfDay(startDateTime), {
                        shouldValidate: true,
                        shouldDirty: true
                      });
                    }

                    if (endDateTime) {
                      form.setValue('endDateTime', endOfDay(endDateTime), {
                        shouldValidate: true,
                        shouldDirty: true
                      });
                    }
                  }
                }}
              />
              {hasTimeChangeValidations && !disabled && session && session.isPublished && (
                <TimeChangeWarningBanner onClick={(): void => setIsTimeChangeModalOpen(true)} />
              )}
            </div>
          </div>

          <EventFormatFieldsSection disabled={disabled} timezoneCode={event.timezoneCode} />

          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <ActionButton onClick={() => setCapacityModalOpen(true)} disabled={disabled}>
              <ActionButtonIcon theme="primary">
                <ArrowUpIcon size={24} />
              </ActionButtonIcon>
              <ActionButtonLabel>
                Capacity
                <ActionButtonCaption>
                  {capacity?.maxAttendees ? `${capacity.maxAttendees}` : 'Unlimited'}
                </ActionButtonCaption>
              </ActionButtonLabel>
            </ActionButton>
            <ActionButton onClick={() => setTargetAttendeesModalOpen(true)} disabled={disabled}>
              <ActionButtonIcon theme="primary">
                <PlusCircleIcon size={24} />
              </ActionButtonIcon>
              <ActionButtonLabel>
                Target attendees
                <ActionButtonCaption>
                  {targetAttendance ? `${targetAttendance}` : 'None'}
                </ActionButtonCaption>
              </ActionButtonLabel>
            </ActionButton>
          </div>
        </form>
      </Form>

      <CapacityModal
        open={capacityModalOpen}
        onOpenChange={setCapacityModalOpen}
        initialValues={
          form.getValues('capacity') ?? {
            maxAttendees: null,
            capacityType: CapacityType.UNLIMITED
          }
        }
        onSubmit={(values) => {
          form.setValue('capacity', values, {
            shouldValidate: true,
            shouldDirty: true
          });
          setCapacityModalOpen(false);
        }}
      />

      <TargetAttendeesModal
        open={targetAttendeesModalOpen}
        onOpenChange={setTargetAttendeesModalOpen}
        initialValues={{
          targetAttendance: form.getValues('targetAttendance')
        }}
        onSubmit={(value) => {
          form.setValue('targetAttendance', value.targetAttendance, {
            shouldValidate: true,
            shouldDirty: true
          });
          setTargetAttendeesModalOpen(false);
        }}
      />

      <TimeChangeConfirmModal
        open={isTimeChangeModalOpen}
        onOpenChange={setIsTimeChangeModalOpen}
        type={TimeChangeConfirmModalType.DISPLAY}
        isSession
        timeChangeValidations={timeChangeValidations}
        onReschedule={handleTimeChangeReset}
      />
    </React.Fragment>
  );
};

export { EventSessionForm };
